import { useState } from 'react';
import { Play } from 'lucide-react';
import { SoteriaCard, soteriaCardCtaClass } from './SoteriaCard';
import type { BacktestConfirm } from '../hooks/useSoteriaChat';

/**
 * BacktestConfirmCard
 * -------------------
 * The "run it" step, made explicit.
 *
 * A build no longer ends in a backtest. "Backtest it from 2019 to 2024" is how
 * an advisor states a WINDOW, and reading it as an instruction to run meant a
 * strategy was tested — spending one of the month's runs — before anyone had
 * looked at what was built. No wording tells those two apart reliably, so the
 * decision is handed back: the strategy card says what exists, this one asks
 * whether to test it.
 *
 * Clicking sends "run the backtest" as the next turn, which is the same request
 * the advisor could type. Shown only on the latest turn (see
 * SoteriaMessageList), so the transcript never accumulates live buttons on old
 * replies.
 */
export function BacktestConfirmCard({
  card,
  onConfirm,
}: {
  card: BacktestConfirm;
  onConfirm: (value: string) => void;
}) {
  const [sent, setSent] = useState(false);

  return (
    <SoteriaCard
      variant="default"
      icon={<Play className="h-4 w-4" />}
      eyebrow="No backtest has been run yet"
      title={card.strategy_name ?? 'Your strategy'}
      subtitle={
        sent
          ? 'Starting the backtest...'
          : 'Run it over the window saved with this strategy.'
      }
      cta={
        <button
          type="button"
          disabled={sent}
          onClick={() => {
            setSent(true);
            onConfirm('run the backtest');
          }}
          className={`${soteriaCardCtaClass('default')} disabled:opacity-50`}
        >
          <Play className="h-3 w-3" />
          Run backtest
        </button>
      }
    />
  );
}
